import { createSelector } from 'reselect';
import { APP_MODULES, CURRENT_YEAR } from 'const';
import { getUserPackagesFromModules } from 'modules/loggedUserInfo/helpers';

const {
  APPROVE_INVOICE,
  READONLY_INVOICE,
  PAY_INVOICE,
  APPROVE_AND_PAY_INVOICE,
  ADDITIONAL_USER,
  DASHBOARD_HR,
  PRE_VALIDATE_INVOICE,
} = APP_MODULES;

export const selectUserTermsOfUseAcceptance = (state) =>
  state.loggedUserInfo?.info?.data?.user?.termsAccepted;

export const selectUserCompanies = (state) =>
  state.loggedUserInfo?.info?.data?.companies || [];

export const selectUserCompaniesListData = createSelector(
  [selectUserCompanies],
  (companies) => companies.map(({ id, name }) => ({ id, name }))
);

export const selectUserInfoStatus = (state) =>
  state.loggedUserInfo?.info?.status;

export const selectUserNPS = (state) =>
  state.loggedUserInfo?.info?.data?.user?.nps || [];

export const selectUserPreValidateLimit = (state) =>
  state.loggedUserInfo?.info?.data?.user?.preValidateLimit;

export const selectUserNotifications = (state) =>
  state.loggedUserInfo?.info?.data?.user?.notifications || [];

export const selectUserNotificationIndex = (state) =>
  state.loggedUserInfo?.notificationIndex;

export const selectUserNotification = createSelector(
  [selectUserNotifications, selectUserNotificationIndex],
  (notifications, index) => notifications[index] || {}
);

export const selectIsUserNotification = createSelector(
  [selectUserNotifications, selectUserNotificationIndex],
  (notifications, index) => !!notifications[index]
);

export const selectIsUserNextNotification = createSelector(
  [selectUserNotifications, selectUserNotificationIndex],
  (notifications, index) => !!notifications[index + 1]
);

export const selectUserInfoFullName = (state) =>
  `${
    state.loggedUserInfo?.info?.data?.user?.firstName
  } ${state.loggedUserInfo?.info?.data?.user?.lastName}`;

export const selectUserCurrentCompany = (state) =>
  state.loggedUserInfo?.info?.data?.current || {};

export const selectUserCurrentCompanyPreCompta = (state) =>
  state.loggedUserInfo?.info?.data?.current?.preCompta;

export const selectUserCurrentCompanyPreComptaMailCollector = (state) =>
  state.loggedUserInfo?.info?.data?.current?.preComptaMailCollector;

export const selectUserCurrentCompanyPreComptaMail = (state) =>
  state.loggedUserInfo?.info?.data?.current?.preComptaMail;

export const selectUserCurrentCompanyDashboardInfo = (state) =>
  state.loggedUserInfo?.info?.data?.companiesDashboardData[0];

export const selectUserCurrentCompanyExercice = createSelector(
  [selectUserCurrentCompanyDashboardInfo],
  (currentCompanyInfo) =>
    currentCompanyInfo?.data ? currentCompanyInfo.data.currentExercice : ''
);

export const selectUserCurrentCompanyExerciceWithYear = createSelector(
  [selectUserCurrentCompanyExercice],
  (currentExercice) => ({
    currentExercice,
    currentYearFromExercice:
      (currentExercice && currentExercice.slice(0, 4)) || CURRENT_YEAR,
  })
);

export const selectUserCurrentCompanyMaxPeriod = createSelector(
  [selectUserCurrentCompanyDashboardInfo],
  (currentCompanyInfo) =>
    currentCompanyInfo?.data ? currentCompanyInfo.data.maxPeriod : ''
);

export const selectUserCurrentCompanyTransition = createSelector(
  [selectUserCurrentCompanyDashboardInfo],
  (currentCompanyInfo) =>
    currentCompanyInfo?.data ? currentCompanyInfo.data.inTransition : ''
);

export const selectUserCurrentCompanyDosId = (state) =>
  selectUserCurrentCompany(state)?.id;

export const selectUserCurrentCompanyActivatedStatus = (state) =>
  selectUserCurrentCompany(state)?.activated;

export const selectUserCurrentCompanyReliability = createSelector(
  [selectUserCurrentCompanyDashboardInfo],
  (currentCompanyInfo) => currentCompanyInfo?.data?.reliabilityIndex || 'N/A'
);

export const selectUserPreviewFinancials = (state) =>
  state.loggedUserInfo?.info?.data?.financials || {};

export const selectEmailOfHrManager = (state) =>
  state.loggedUserInfo?.info?.data?.current?.emailOfHRManager;

export const selectUserEmail = (state) =>
  state.loggedUserInfo?.info?.data?.user?.email;

export const selectUserAuthMethod = (state) =>
  state.loggedUserInfo?.info?.data?.user?.authMethod;

export const selectIsXPBCompany = (state) =>
  state.loggedUserInfo?.info?.data?.current?.managedXpb;

export const selectUserCompanyId = (state) =>
  state.loggedUserInfo?.info?.data?.current?.id;

export const selectUserAuthorizedModules = (state) =>
  state.loggedUserInfo?.info?.data?.authorizedModules;

export const selectIsMainUser = (state) =>
  state.loggedUserInfo?.info?.data?.user?.isMainUser;

export const selectIsEDocActive = (state) =>
  state.loggedUserInfo?.info?.data?.current?.isEDocActive;

export const selectUserPackagesNames = createSelector(
  [selectUserAuthorizedModules],
  (authorizedModules) => getUserPackagesFromModules(authorizedModules)
);

export const selectCanOnlyView = createSelector(
  [selectUserAuthorizedModules],
  (authorizedModules) => authorizedModules.includes(READONLY_INVOICE)
);

export const selectCanValidate = createSelector(
  [selectUserAuthorizedModules],
  (authorizedModules) =>
    authorizedModules.includes(APPROVE_INVOICE) &&
    !authorizedModules.includes(READONLY_INVOICE)
);

export const selectCanPrevalidate = createSelector(
  [selectUserAuthorizedModules],
  (authorizedModules) => authorizedModules.includes(PRE_VALIDATE_INVOICE)
);

export const selectCanPay = createSelector(
  [selectUserAuthorizedModules],
  (authorizedModules) =>
    [PAY_INVOICE, APPROVE_AND_PAY_INVOICE].some((module) =>
      authorizedModules.includes(module)
    )
);

export const selectCanValidateAndPay = createSelector(
  [selectUserAuthorizedModules],
  (authorizedModules) => authorizedModules.includes(APPROVE_AND_PAY_INVOICE)
);

export const selectUserAdditionalUsers = createSelector(
  [
    (state) => state.loggedUserInfo?.info?.data?.user?.additionalUsers,
    (_, modulesToInclude) => modulesToInclude || [],
    selectUserCurrentCompanyDosId,
  ],
  (additionalUsers, modulesToInclude, currentCompanyId) => {
    if (!additionalUsers) return [];

    return additionalUsers
      .filter((user) => user.companiesId.includes(currentCompanyId))
      .map(({ modules, ...restUser }) => ({
        ...restUser,
        modules,
        tableModules: modules.filter((module) => {
          const isNotExcluded = ![
            ADDITIONAL_USER,
            DASHBOARD_HR,
            PAY_INVOICE,
          ].includes(module);

          if (modulesToInclude.length === 0) {
            return isNotExcluded;
          }

          return modulesToInclude.includes(module) && isNotExcluded;
        }),
      }));
  }
);

export const selectAccountingAccessLink = (state) =>
  state.loggedUserInfo?.accountingLink;
